/**
 * Minor Compliance Helpers — Phase 2E
 *
 * Pure functions only — no Supabase calls here.
 * Import these helpers anywhere age/minor logic is needed.
 *
 * Age buckets:
 *   adult        = 18+         → full access, no restrictions
 *   older_minor  = 16–17       → minor, protected visibility, no extra gating
 *   younger_minor= 13–15       → minor, guardian acknowledgment required
 *   blocked      = under 13    → cannot proceed (COPPA hard block)
 *   unknown      = no birth_year provided (pre-2E users, or skipped)
 *
 * Doctrine: docs/DUEL_MINOR_COMPLIANCE.md
 */

export type MinorStatus = 'adult' | 'older_minor' | 'younger_minor' | 'blocked' | 'unknown';

const CURRENT_YEAR = (): number => new Date().getFullYear();

/**
 * Compute approximate age from a birth year.
 * Uses January 1 of the birth year — intentionally conservative (may read
 * as slightly older than actual age mid-year, which is safe for our gating).
 */
export function getUserAge(birthYear: number): number {
  return CURRENT_YEAR() - birthYear;
}

/**
 * Classify a user into an age bucket.
 *
 * @param birthYear  The user's birth year (e.g. 2007). Pass null/undefined for unknown.
 * @returns MinorStatus bucket
 *
 * Bucket boundaries (inclusive):
 *   under 13 → blocked
 *   13–15    → younger_minor (guardian ack required)
 *   16–17    → older_minor (protected visibility, no extra gating)
 *   18+      → adult
 *   null     → unknown
 */
export function getMinorStatus(birthYear: number | null | undefined): MinorStatus {
  if (birthYear == null) return 'unknown';

  const age = getUserAge(birthYear);

  if (age < 13) return 'blocked';
  if (age <= 15) return 'younger_minor';
  if (age <= 17) return 'older_minor';
  return 'adult';
}

/**
 * Whether guardian consent flow is required for this birth year.
 * True only for younger_minor (13–15).
 */
export function requiresGuardianConsent(birthYear: number | null | undefined): boolean {
  return getMinorStatus(birthYear) === 'younger_minor';
}

/**
 * Whether a user profile has already completed minor compliance.
 *
 * Returns true if:
 * - User is an adult (18+) → compliance not applicable, effectively complete
 * - User is older_minor (16–17) → no extra compliance needed, effectively complete
 * - User is younger_minor (13–15) AND minor_compliance_completed = true
 * - birth_year is unknown → treated as complete (legacy users, don't block)
 */
export function hasCompletedMinorCompliance(profile: {
  birth_year?: number | null;
  minor_compliance_completed?: boolean | null;
}): boolean {
  const status = getMinorStatus(profile.birth_year);

  switch (status) {
    case 'blocked':
      // Should never reach compliance — they're routed to age-blocked screen
      return false;
    case 'younger_minor':
      // Requires explicit guardian acknowledgment
      return profile.minor_compliance_completed === true;
    case 'older_minor':
    case 'adult':
    case 'unknown':
      // No guardian consent required — compliance is effectively complete
      return true;
  }
}

/**
 * Whether a school org requires minor consent from joining users.
 *
 * True if:
 * - org.requires_minor_consent is explicitly true, OR
 * - org.org_type is 'school' (belt-and-suspenders in case DB flag is not set)
 */
export function isSchoolComplianceRequired(org: {
  requires_minor_consent?: boolean | null;
  org_type?: string | null;
}): boolean {
  return org.requires_minor_consent === true || org.org_type === 'school';
}

/**
 * Whether a user should be hard-blocked from proceeding.
 * True if under 13 (COPPA hard block).
 */
export function shouldBlockUser(birthYear: number | null | undefined): boolean {
  return getMinorStatus(birthYear) === 'blocked';
}

/**
 * Whether this user should see the guardian acknowledgment screen.
 *
 * True when:
 * - User is younger_minor (age 13–15)
 * - AND they have NOT yet completed minor compliance
 */
export function shouldRequireGuardianAck(profile: {
  birth_year?: number | null;
  minor_compliance_completed?: boolean | null;
}): boolean {
  if (profile.minor_compliance_completed === true) return false;
  return getMinorStatus(profile.birth_year) === 'younger_minor';
}

/**
 * Validate a birth year input from a user.
 * Returns null if valid, or an error string if invalid.
 *
 * Acceptance range: (current year - 100) to (current year - 5)
 * Rejects obviously invalid values (future years, too old, non-numeric).
 */
export function validateBirthYear(value: string | number): string | null {
  const year = typeof value === 'string' ? parseInt(value, 10) : value;

  if (isNaN(year)) return 'Please enter a valid year';

  const currentYear = CURRENT_YEAR();
  const minYear = currentYear - 100;
  const maxYear = currentYear - 5;

  if (year < minYear) return `Birth year must be after ${minYear}`;
  if (year > maxYear) return `Birth year must be before ${maxYear + 1}`;

  return null; // valid
}
